/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getMaterials } from '../../api/MaterialAPI';
import { AppThunk } from '../../app/store';
import { OptionsType, OptionType } from '../../components/filterTypes/multiselectbox/types';
import { MaterialDTO } from '../../dto/MaterialDTO';

import {
  updateURLQuery,
  getStringQueryArrayParam,
  getQueryParam,
  getQueryArrayParam,
} from '../../components/QueryBuilder';

interface MaterialState {
  key: string;
  label: string;
  type: string;
  values: MaterialDTO[];
  isLoading: boolean;
  error: string | null;
  selectedIds: number[];
  selected: OptionsType | MaterialDTO[];
  isCombobox: boolean;
  inputValue: string;
  isPageLoad: boolean;
  placeholder: string;
  econdaName: string;
  infoButtonDescription: string;
  selectedPimMaterialCodes: string[];
}

const initialState: MaterialState = {
  key: 'materials',
  label: 'materialFilter',
  type: 'multiSelectBox',
  values: [],
  isLoading: false,
  error: null,
  selectedIds: getQueryArrayParam('materialIds', []),
  selected: [],
  isCombobox: true,
  inputValue: '',
  isPageLoad: true, //this parameter is used to know if we should load the selected options from the url
  placeholder: 'searchByMaterialName',
  econdaName: 'material',
  infoButtonDescription: 'infoButtonMaterialDescription',
  selectedPimMaterialCodes: getStringQueryArrayParam('pimMaterialCodes', []),
};

function startLoading(state: MaterialState) {
  state.isLoading = true;
}

function loadingFailed(state: MaterialState, action: PayloadAction<string>) {
  state.isLoading = false;
  state.error = action.payload;
}

function updateSelectedMaterials(state: MaterialState, action: PayloadAction<OptionsType>) {
  state.selectedIds = [];
  state.selected = action.payload;
  action.payload.forEach((materialSelected) => {
    state.selectedIds.push(materialSelected.id);
  });
  updateURLQuery('materialIds', state.selectedIds);
}

function updateInputData(state: MaterialState, action: PayloadAction<string>) {
  state.inputValue = action.payload;
}

function updateFromCategory(state: MaterialState): void {
  const materialCategoryIds = getQueryParam('materialCategoryIds', '');
  const selectedTemp: OptionsType = [];
  const selectedIdTemp: number[] = [];
  if (materialCategoryIds !== '') {
    state.selectedIds.forEach((selectedId) => {
      const selectedMaterial = state.values.find((value) => value.id === Number(selectedId));
      if (selectedMaterial) {
        selectedTemp.push(selectedMaterial);
        selectedIdTemp.push(Number(selectedMaterial.id));
      }
    });
  }
  state.selected = selectedTemp;
  state.selectedIds = selectedIdTemp;
  updateURLQuery('materialIds', state.selectedIds);
}

function getValueFromSelectedId(state: MaterialState): void {
  if (state.isPageLoad) {
    state.selected = [];
    state.selectedIds.forEach((element) => {
      const selectedElement = state.values.find((value) => value.id === element);
      if (selectedElement) {
        state.selected.push(selectedElement);
      }
    });

    state.selectedPimMaterialCodes.forEach((element) => {
      if (element.endsWith('_INCH')) {
        const trimInchesFromElement = element.slice(0, -5);
        element = trimInchesFromElement;
      }
      const selectedElement = state.values.find((value) => value.pimMaterialCode === element);
      if (selectedElement && !state.selected.includes(selectedElement)) {
        state.selected.push(selectedElement);
        state.selectedIds.push(selectedElement.id);
      }
    });

    updateURLQuery('materialIds', state.selectedIds);
    updateURLQuery('pimMaterialCodes', []);
    state.isPageLoad = false;
  }
}

export const materialsSlice = createSlice({
  name: 'materials',
  initialState,
  reducers: {
    getMaterialsStart: startLoading,
    getMaterialsSuccess(state, { payload }: PayloadAction<any>) {
      state.values = payload.materials;
      if (payload.isMaterialCategoryChange) {
        updateFromCategory(state);
      }
      getValueFromSelectedId(state);
      state.isLoading = false;
      state.error = null;
    },
    getMaterialsFailure: loadingFailed,
    updateMaterialsSelected: updateSelectedMaterials,
    updateMaterialsInput: updateInputData,
  },
});

export const {
  getMaterialsStart,
  getMaterialsSuccess,
  getMaterialsFailure,
  updateMaterialsSelected,
  updateMaterialsInput,
} = materialsSlice.actions;

export default materialsSlice.reducer;

export const fetchMaterials =
  (query: string, isMaterialCategoryChange: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getMaterialsStart());
      const materials = await getMaterials(query);
      dispatch(getMaterialsSuccess({ materials, isMaterialCategoryChange }));
    } catch (err) {
      if (err instanceof Error) {
        dispatch(getMaterialsFailure(err.message));
      } else {
        dispatch(getMaterialsFailure("Can't dispatch getMaterials"));
      }
    }
  };
